import React, { useEffect, useMemo, useState } from "react";
import { Flame, Filter as FilterIcon, X as XIcon, RotateCw, ArrowRight, Activity } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fehlerText, api as axiosApi } from "@/lib/api";
import HoverPreview from "@/components/HoverPreview";

/**
 * Tactic Density Heatmap.
 *
 * Pulls every calibrated YouTube tactic from `/api/analytics/passing-flow-map`
 * and renders one point per tactic on a normalised 100×37 m pitch outline.
 * Each tactic's `brick_image_xy` (admin-placed point of interest, fine-tunable
 * via drag) is the signal — projected through its homography into normalised
 * canonical-field coordinates by the backend.
 *
 * Visual density is achieved organically: each point is a small filled circle
 * with low opacity, so overlaps brighten the field naturally. A tag filter
 * narrows the view to a single play type (e.g. "huck" hotspots).
 *
 * MVP scope — no Gaussian KDE blur yet; we add that once we have ≥ 50 tactics
 * to justify the cost. Until then organic alpha-blending tells the story just
 * as well and ships today.
 */
export default function Heatmap({ fieldDims = null } = {}) {
  // 🆕 2026-02-28 v7 · Dynamic field dimensions. Caller passes
  // ``fieldDims = { length, width, endzone, brick }`` (numbers in
  // metres) when known — typically derived from the open sequence's
  // game_config. When no prop is provided we hydrate from the
  // server's resolved aggregate (``data.field`` block · Phase E
  // v7c) so the Dashboard renders against the active match's true
  // profile (U14 50×20, U17 64×25, etc.) instead of the legacy
  // adult fallback. The viewBox scales the metres → 10 pixels so
  // a 50×20 m U14 pitch renders at 500×200 viewBox units while a
  // 95×35 adult pitch renders at 950×350. The aspect ratio varies
  // naturally; ``preserveAspectRatio="xMidYMid meet"`` on the <svg>
  // keeps the rendering centered + uncropped.
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  // Multi-select tag-chip filter. Empty Set ⇒ "all" (no filtering).
  // Selecting multiple chips applies AND semantics — a tactic must
  // carry EVERY active tag to make it onto the canvas. This matches
  // coach intent: "show me Hucks AND from the Endzone".
  const [activeTags, setActiveTags] = useState(() => new Set());
  // Hover preview — small YouTube thumbnail floating near the cursor when
  // the admin hovers a dot or arrow. Position is in viewBox units so it
  // tracks the SVG layer reliably across resizes.
  const [hover, setHover] = useState(null);  // {kind:'dot'|'arrow', item, cx, cy}
  // Rotate-Field toggle (180° around the canvas centre). A pure horizontal
  // mirror (`scaleX(-1)`) is tactically WRONG — flipping only the length
  // axis swaps the relative sidelines so a coach reading a "break-side"
  // pass after toggling would mistake it for an open-side pass. Real
  // coaches mentally rotate the pitch 180° when the team changes attack
  // direction; that operation preserves "left-of-running-direction" as
  // "left-of-running-direction" for every clip. So we do the same here:
  // `rotate(180, cx, cy)` flips both axes around the centre, leaving every
  // tactical pattern invariant relative to the running direction.
  // Persisted to localStorage so the coach's preference survives reload.
  const [rotated, setRotated] = useState(() => {
    try { return localStorage.getItem("heatmap-rotate") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("heatmap-rotate", rotated ? "1" : "0"); }
    catch { /* incognito / sandbox — silently ignore */ }
  }, [rotated]);

  // Render-mode toggle: arrows-per-pass (default, granular) vs density-
  // heatmap (aggregate hot-spots). Two complementary lenses on the same
  // dataset — the arrows let a coach trace individual play-shapes, while
  // the density cloud surfaces "where is our offence actually clustering
  // on the pitch?" without the per-clip noise. Persisted to localStorage
  // so the coach's lens-choice survives reload.
  const [renderMode, setRenderMode] = useState(() => {
    try { return localStorage.getItem("heatmap-render-mode") === "density" ? "density" : "arrows"; }
    catch { return "arrows"; }
  });
  useEffect(() => {
    try { localStorage.setItem("heatmap-render-mode", renderMode); }
    catch { /* incognito / sandbox — silently ignore */ }
  }, [renderMode]);
  const navigate = useNavigate();

  // Click → jump to the correct editor based on where the vector/dot was
  // born. YT-tactics open in the YouTube calibrator (`/admin/tactics?focus=…`)
  // while match-clip vectors open the SequenceLab for the parent match
  // (`/admin/wizard/<match_id>?clip=<clip_id>`) so the admin lands inside
  // the row with the Pass-Vector toggle already auto-expanded. Anything
  // unrecognised falls back to the tactics page so we never throw.
  const jumpToItem = (item) => {
    if (!item?.id) return;
    if (item.source === "clip") {
      if (!item.match_id) {
        // Defensive: clip without parent is a data bug — surface a console
        // warning instead of routing nowhere.
        console.warn("Heatmap: clip vector missing match_id", item);
        return;
      }
      navigate(`/admin/wizard/${encodeURIComponent(item.match_id)}?clip=${encodeURIComponent(item.id)}`);
      return;
    }
    // Default → YT tactic (source === "tactic" or undefined for legacy points)
    navigate(`/admin/tactics?focus=${encodeURIComponent(item.id)}`);
  };

  useEffect(() => {
    let cancelled = false;
    axiosApi.get("/analytics/passing-flow-map")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(fehlerText(e) || "Failed to load heatmap"); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (activeTags.size === 0) return data.points;
    // AND semantics — every selected tag must be present.
    return data.points.filter((p) =>
      [...activeTags].every((t) => (p.tags || []).includes(t)),
    );
  }, [data, activeTags]);

  const filteredVectors = useMemo(() => {
    if (!data) return [];
    const all = data.vectors || [];
    if (activeTags.size === 0) return all;
    return all.filter((v) =>
      [...activeTags].every((t) => (v.tags || []).includes(t)),
    );
  }, [data, activeTags]);

  // Per-tag counts (across both dots + arrows) so the chip can show
  // "Zone (4)" without re-traversing on every render.
  const tagCounts = useMemo(() => {
    if (!data) return new Map();
    const m = new Map();
    const bump = (t) => m.set(t, (m.get(t) || 0) + 1);
    (data.points || []).forEach((p) => (p.tags || []).forEach(bump));
    (data.vectors || []).forEach((v) => (v.tags || []).forEach(bump));
    return m;
  }, [data]);

  const toggleTag = (t) => {
    setActiveTags((prev) => {
      const next = new Set(prev);
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });
  };
  const clearTags = () => setActiveTags(new Set());

  // SVG viewBox: scaled length × width. Aspect varies per division.
  // 10 viewBox units = 1 metre — clean math for tick spacings + the
  // endzone/brick rectangles below. The label in the footer reads
  // from the same `dims` so the user always sees the SAME numbers
  // the SVG is rendered with.
  //
  // Resolution chain (Phase E v7c · 2026-02-28):
  //   1. Explicit ``fieldDims`` prop (when callers know the active
  //      sequence's profile and want hard control).
  //   2. Server-resolved aggregate ``data.field`` block — Smart-Default
  //      picks the unique profile across contributing sequences, or
  //      falls back to Adult + ``mixed=true`` for multi-profile mixes.
  //   3. Legacy hard-coded 95×35 m Adult fallback while the API is
  //      still loading or returned no field block.
  const apiField = data && data.field ? data.field : null;
  const dims = fieldDims || (apiField ? {
    length: Number(apiField.length_m) || 95,
    width: Number(apiField.width_m) || 35,
    endzone: Number(apiField.endzone_depth_m) || 18,
    brick: Number(apiField.brick_distance_m) || 20,
  } : { length: 95, width: 35, endzone: 18, brick: 20 });
  const profileLabel = !fieldDims && apiField ? apiField.profile_label : null;
  const profileMixed = !fieldDims && apiField ? !!apiField.mixed : false;
  const VB_W = Math.round(dims.length * 10);
  const VB_H = Math.round(dims.width * 10);
  const EZ = Math.round(dims.endzone * 10);
  const BRICK = Math.round(dims.brick * 10);

  return (
    <div className="border border-border bg-card rounded-sm p-5" data-testid="tactic-heatmap-widget">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-2 shrink-0">
          <Flame className="w-4 h-4 text-pink-400" />
          <h3 className="font-display font-black uppercase tracking-wider text-base">
            Passing-Flow-Map
          </h3>
          <button
            data-testid="heatmap-mirror-toggle"
            data-active={rotated ? "true" : undefined}
            onClick={() => setRotated((r) => !r)}
            title={rotated ? "Restore canonical attack direction" : "Rotate the pitch 180° to view both attack directions in the same mental frame (relative sidelines preserved — break-side stays break-side, open-side stays open-side)"}
            className={`inline-flex items-center gap-1 ml-1 px-1.5 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-widest transition-colors border ${
              rotated
                ? "bg-pink-400/15 text-pink-300 border-pink-400/60 hover:bg-pink-400/25"
                : "bg-secondary text-muted-foreground border-border hover:border-pink-400/60 hover:text-pink-300"
            }`}
          >
            <RotateCw className={`w-3 h-3 transition-transform duration-300 ${rotated ? "rotate-180" : ""}`} />
            <span>Direction Switch</span>
          </button>
          {/* Render-mode toggle. ARROWS view = per-pass vectors (great for
              tracing individual play-shapes). DENSITY view = 2D Gauss-
              blurred hot-spot cloud over release+catch coordinates (great
              for surfacing aggregate offence patterns without per-clip
              clutter). Single toggle swaps the layer; everything else
              (filters, rotation, hover-cards) stays identical. */}
          <button
            data-testid="heatmap-render-mode-toggle"
            data-mode={renderMode}
            onClick={() => setRenderMode((m) => (m === "arrows" ? "density" : "arrows"))}
            title={renderMode === "density"
              ? "Switch to per-pass arrows — trace individual play-shapes"
              : "Switch to density heatmap — surface aggregate hot-spots across all passes"}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-widest transition-colors border ${
              renderMode === "density"
                ? "bg-orange-400/15 text-orange-300 border-orange-400/60 hover:bg-orange-400/25"
                : "bg-secondary text-muted-foreground border-border hover:border-orange-400/60 hover:text-orange-300"
            }`}
          >
            {renderMode === "density"
              ? <Activity className="w-3 h-3" />
              : <ArrowRight className="w-3 h-3" />}
            <span>{renderMode === "density" ? "Heatmap (Density)" : "Arrows"}</span>
          </button>
        </div>
        {data && data.tags.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 justify-end max-w-[70%]">
            <FilterIcon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            {data.tags.map((t) => {
              const active = activeTags.has(t);
              const cnt = tagCounts.get(t) || 0;
              return (
                <button
                  key={t}
                  data-testid={`heatmap-tag-chip-${t}`}
                  data-active={active ? "true" : undefined}
                  onClick={() => toggleTag(t)}
                  className={`group inline-flex items-center gap-1 px-2 py-0.5 rounded-sm font-mono text-[10px] uppercase tracking-widest transition-colors ${
                    active
                      ? "bg-pink-400 text-black border border-pink-400 hover:bg-pink-300"
                      : "bg-secondary text-muted-foreground border border-border hover:border-pink-400/60 hover:text-pink-300"
                  }`}
                >
                  {t}
                  <span className={`tabular-nums ${active ? "text-black/60" : "text-muted-foreground/60"}`}>
                    {cnt}
                  </span>
                  {active && <XIcon className="w-3 h-3 ml-0.5 -mr-1 opacity-70 group-hover:opacity-100" />}
                </button>
              );
            })}
            {activeTags.size > 0 && (
              <button
                data-testid="heatmap-tag-clear"
                onClick={clearTags}
                className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-pink-300 underline underline-offset-2 ml-1"
                title="Clear all tag filters"
              >
                clear
              </button>
            )}
          </div>
        )}
      </div>

      {err && (
        <div className="text-destructive font-mono text-xs">{err}</div>
      )}

      {!data && !err && (
        <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest">
          Loading tactic density…
        </div>
      )}

      {data && (
        <>
          <div className="relative w-full overflow-hidden rounded-sm border border-border bg-[#0a1f15]">
            <svg viewBox={`0 0 ${VB_W} ${VB_H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
              {/* Arrowhead marker — re-used by every pass-vector line. Lives
                  outside the mirror group so the orient="auto-start-reverse"
                  computation stays in raw viewBox space. */}
              <defs>
                <marker id="flow-arrow" viewBox="0 0 12 12"
                        refX="10" refY="6" markerWidth="9" markerHeight="9"
                        orient="auto-start-reverse">
                  <path d="M 0 0 L 12 6 L 0 12 L 3 6 Z" fill="rgba(255,89,178,0.95)" />
                </marker>
                <marker id="flow-arrow-dim" viewBox="0 0 12 12"
                        refX="10" refY="6" markerWidth="9" markerHeight="9"
                        orient="auto-start-reverse">
                  <path d="M 0 0 L 12 6 L 0 12 L 3 6 Z" fill="rgba(255,89,178,0.55)" />
                </marker>
                {/* ── Density-heatmap filter ─────────────────────────────
                    Pipeline:
                      1. `feGaussianBlur` smears every release/catch dot
                         into a soft cloud. The deviation is tuned so
                         single isolated points are clearly visible but
                         tight clusters fuse into bright hot-spots.
                      2. `feComponentTransfer` maps the blurred alpha to
                         a perceptual Blue → Cyan → Green → Yellow →
                         Orange → Red colormap (the classic thermal
                         palette every coach can read instantly).
                      Overlapping dots additively boost their region's
                      alpha after the blur, so the colormap naturally
                      paints stacks RED without us doing manual binning.
                    Why feComponentTransfer (not feColorMatrix):
                      tableValues lets us hand-pick the colour stops, so
                      the transition from cool→hot is non-linear and
                      reads correctly to the human eye. A linear matrix
                      would wash out the mid-greens. */}
                <filter id="heatmap-density-filter"
                        x="-30%" y="-30%" width="160%" height="160%"
                        colorInterpolationFilters="sRGB">
                  <feGaussianBlur stdDeviation="30" />
                  <feComponentTransfer>
                    {/* 9-stop ramp for a smoother Blue → Cyan → Teal →
                        Green → Yellow → Orange → Red transition. The
                        alpha curve is gentle on the low end so cool-zone
                        haloes ARE rendered (not invisible) — with sparse
                        data the human eye still gets the full thermal
                        palette around each hotspot. */}
                    <feFuncR type="table"
                             tableValues="0    0    0    0    0    0.2  0.6  0.95 1   " />
                    <feFuncG type="table"
                             tableValues="0    0.15 0.45 0.75 0.95 0.95 0.7  0.35 0   " />
                    <feFuncB type="table"
                             tableValues="0.2  0.85 1    0.85 0.5  0.1  0    0    0   " />
                    <feFuncA type="table"
                             tableValues="0    0.18 0.4  0.6  0.75 0.85 0.92 0.97 1   " />
                  </feComponentTransfer>
                </filter>
                {/* Soft radial gradient for each density-mode "intensity
                    dot". Wide spread + low peak alpha so the Gaussian
                    blur produces a tall pyramid of alpha values — the
                    full thermal colormap (cool blue → hot red) is
                    visible around every single point, even with only
                    one calibrated pass in the dataset. */}
                <radialGradient id="heatmap-point-gradient">
                  <stop offset="0%"   stopColor="white" stopOpacity="0.55" />
                  <stop offset="45%"  stopColor="white" stopOpacity="0.30" />
                  <stop offset="100%" stopColor="white" stopOpacity="0" />
                </radialGradient>
              </defs>

              {/* Rotate group — wraps everything that lives in canonical
                  field-space so a single `rotate(180 cx cy)` transform
                  flips the whole pitch 180° around its centre. This
                  preserves left-of-running-direction as left-of-running-
                  direction (a pure horizontal mirror would swap sidelines
                  and ruin the tactical reading). Text labels live OUTSIDE
                  this group so they stay readable; their content swaps
                  with the rotate state so the geometry STILL matches the
                  words. */}
              <g transform={rotated ? `rotate(180 ${VB_W / 2} ${VB_H / 2})` : undefined}>
                {/* Field outline + endzone shading */}
                <rect x="0" y="0" width={VB_W} height={VB_H} fill="#0a1f15" />
                <rect x="0" y="0" width={EZ} height={VB_H}
                      fill="rgba(0,230,150,0.05)" stroke="rgba(0,230,150,0.3)" strokeWidth="1" />
                <rect x={VB_W - EZ} y="0" width={EZ} height={VB_H}
                      fill="rgba(0,230,150,0.05)" stroke="rgba(0,230,150,0.3)" strokeWidth="1" />
                {/* Outer pitch border */}
                <rect x="1" y="1" width={VB_W - 2} height={VB_H - 2}
                      fill="none" stroke="rgba(0,230,150,0.5)" strokeWidth="2" />
                {/* Goal lines */}
                <line x1={EZ} y1="0" x2={EZ} y2={VB_H} stroke="rgba(0,230,150,0.4)" strokeWidth="1.5" />
                <line x1={VB_W - EZ} y1="0" x2={VB_W - EZ} y2={VB_H} stroke="rgba(0,230,150,0.4)" strokeWidth="1.5" />
                {/* Brick marks (small ticks at 20m past each goalline, centerline only) */}
                <circle cx={EZ + BRICK} cy={VB_H / 2} r="3" fill="rgba(255,89,178,0.6)" />
                <circle cx={VB_W - EZ - BRICK} cy={VB_H / 2} r="3" fill="rgba(255,89,178,0.6)" />
                {/* Midline (decorative) */}
                <line x1={VB_W / 2} y1={VB_H * 0.3} x2={VB_W / 2} y2={VB_H * 0.7}
                      stroke="rgba(0,230,150,0.2)" strokeWidth="1" strokeDasharray="4 4" />

                {/* ── Render-mode branch ─────────────────────────────────
                    Either per-pass arrows (granular, tracing play-shapes)
                    OR a density cloud (aggregate hot-spots). Same dataset
                    in both cases — we just feed it into a different SVG
                    layer. */}
                {renderMode === "density" ? (
                  <>
                    {/* All release + catch points fed through the density
                        filter. Each point is rendered as a soft radial
                        gradient circle; the filter blurs them together
                        and remaps the resulting alpha to the Blue → Cyan
                        → Green → Yellow → Orange → Red colormap.
                        Pointer-events are disabled on the density layer
                        because the heatmap is INTENTIONALLY a non-
                        clickable aggregate view — coaches can switch
                        back to Arrows mode to drill into a single pass. */}
                    <g filter="url(#heatmap-density-filter)" pointerEvents="none">
                      {filteredVectors.flatMap((v, i) => {
                        // Each vector renders TWO hot-spots (release +
                        // catch). Keys must be unique within the
                        // flat-mapped output → include BOTH the loop
                        // index and the vector id so a future backend
                        // hiccup that ships duplicate ids (or a
                        // null-id YT tactic) can't collapse two
                        // children onto the same React key (the
                        // dashboard's "two children with the same key"
                        // console warning fixed 2026-02-26).
                        const k = `${i}-${v.id ?? "noid"}`;
                        const r = 55;   // wide enough that the cool-zone halo
                                        // (blue/cyan/green) is visible around
                                        // each single hot-spot.
                        return [
                          <circle key={`d-s-${k}`}
                                  cx={v.start.x * VB_W} cy={v.start.y * VB_H}
                                  r={r} fill="url(#heatmap-point-gradient)" />,
                          <circle key={`d-e-${k}`}
                                  cx={v.end.x * VB_W} cy={v.end.y * VB_H}
                                  r={r} fill="url(#heatmap-point-gradient)" />,
                        ];
                      })}
                      {/* YT-tactic dots (no vector — single brick play) are
                          also valid intensity centres. */}
                      {filtered.map((p, i) => (
                        <circle key={`d-p-${i}-${p.id ?? "noid"}`}
                                cx={p.x * VB_W} cy={p.y * VB_H}
                                r={55} fill="url(#heatmap-point-gradient)" />
                      ))}
                    </g>
                  </>
                ) : (
                  <>
                    {/* Passing-Flow-Map: arrows for tactics with a pass_vector. */}
                    {filteredVectors.map((v, i) => {
                      const x1 = v.start.x * VB_W;
                      const y1 = v.start.y * VB_H;
                      const x2 = v.end.x * VB_W;
                      const y2 = v.end.y * VB_H;
                      const dim = filteredVectors.length > 12;
                      const midX = (x1 + x2) / 2;
                      const midY = (y1 + y2) / 2;
                      const hoverCx = rotated ? (VB_W - midX) : midX;
                      const hoverCy = rotated ? (VB_H - midY) : midY;
                      const onEnter = () => setHover({
                        kind: "arrow", item: v,
                        cx: hoverCx, cy: hoverCy,
                      });
                      const onLeave = () => setHover((h) => (h && h.item?.id === v.id ? null : h));
                      return (
                        <g key={`vec-${i}-${v.id ?? "noid"}`} data-testid={`heatmap-vector-${v.id}`}
                           className="cursor-pointer"
                           onClick={() => jumpToItem(v)}
                           onMouseEnter={onEnter}
                           onMouseLeave={onLeave}>
                          <line x1={x1} y1={y1} x2={x2} y2={y2}
                                stroke="rgba(0,0,0,0)" strokeWidth="22" strokeLinecap="round" />
                          <line x1={x1} y1={y1} x2={x2} y2={y2}
                                stroke="rgba(255,89,178,0.10)" strokeWidth="14"
                                strokeLinecap="round" pointerEvents="none" />
                          <line x1={x1} y1={y1} x2={x2} y2={y2}
                                stroke={hover?.item?.id === v.id ? "#FF59B2" : (dim ? "rgba(255,89,178,0.55)" : "rgba(255,89,178,0.9)")}
                                strokeWidth={hover?.item?.id === v.id ? "3.4" : "2.4"}
                                strokeLinecap="round" pointerEvents="none"
                                markerEnd={`url(#${dim ? "flow-arrow-dim" : "flow-arrow"})`} />
                          <circle cx={x1} cy={y1} r={hover?.item?.id === v.id ? "5.5" : "4.5"}
                                  fill="rgba(255,89,178,0.95)"
                                  stroke="rgba(255,255,255,0.5)" strokeWidth="1"
                                  pointerEvents="none" />
                          {/* 🆕 Kalibrier-Güte-Badge: Ring am Release-
                              Punkt, Farbe = Ampel der H-Quelle (Selfcal-
                              RMS bzw. Anker-Ampel). Kein Ring = keine
                              Güte-Info (Rect-Legacy / YT-Tactic). */}
                          {v.calib_quality?.grade && (
                            <circle cx={x1} cy={y1} r="8.5" fill="none"
                                    data-testid={`heatmap-vector-quality-${v.id}`}
                                    data-grade={v.calib_quality.grade}
                                    stroke={v.calib_quality.grade === "gruen" ? "#34d399"
                                      : v.calib_quality.grade === "gelb" ? "#fbbf24" : "#fb7185"}
                                    strokeWidth="1.8" opacity="0.9"
                                    pointerEvents="none">
                              <title>{`Kalibrier-Güte: ${v.calib_quality.label || v.calib_quality.grade}${v.calib_quality.detail ? ` · ${v.calib_quality.detail}` : ""}${v.calib_quality.tracked ? " · 📹 Kamera getrackt (Optical Flow)" : ""}`}</title>
                            </circle>
                          )}
                        </g>
                      );
                    })}

                    {/* Heat points — single-dot YT tactics (no vector). */}
                    {filtered.map((p, i) => {
                      const cx = p.x * VB_W;
                      const cy = p.y * VB_H;
                      const hoverCx = rotated ? (VB_W - cx) : cx;
                      const hoverCy = rotated ? (VB_H - cy) : cy;
                      const onEnter = () => setHover({ kind: "dot", item: p, cx: hoverCx, cy: hoverCy });
                      const onLeave = () => setHover((h) => (h && h.item?.id === p.id ? null : h));
                      return (
                        <g key={`pt-${i}-${p.id ?? "noid"}`} data-testid={`heatmap-point-${p.id}`}
                           className="cursor-pointer"
                           onClick={() => jumpToItem(p)}
                           onMouseEnter={onEnter}
                           onMouseLeave={onLeave}>
                          <circle cx={cx} cy={cy} r="22" fill="rgba(255,89,178,0.18)" />
                          <circle cx={cx} cy={cy} r={hover?.item?.id === p.id ? "8" : "6"}
                                  fill="rgba(255,89,178,0.95)"
                                  stroke={hover?.item?.id === p.id ? "#fff" : "rgba(255,255,255,0.4)"}
                                  strokeWidth="1" pointerEvents="none" />
                        </g>
                      );
                    })}
                  </>
                )}
              </g>

              {/* Axis labels — outside the rotate group so the text stays
                  upright + readable. After a 180° rotation the LEFT side
                  of the canvas now visually shows what used to be the
                  RIGHT side of the pitch, so we swap the labels' content
                  (not their position) to keep the words pointing at the
                  correct end. */}
              <text x="8" y={VB_H - 8} fill="rgba(0,230,150,0.4)" fontSize="11" fontFamily="ui-monospace, monospace">
                {rotated ? "← far endzone" : "← back endzone"}
              </text>
              <text x={VB_W - 130} y={VB_H - 8} fill="rgba(0,230,150,0.4)" fontSize="11" fontFamily="ui-monospace, monospace">
                {rotated ? "back endzone →" : "far endzone →"}
              </text>
            </svg>
            {filtered.length === 0 && filteredVectors.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest text-center px-6">
                  {data.points.length === 0 && (data.vectors || []).length === 0
                    ? "No calibrated tactics yet — kalibriere ein paar Plays, dann zeigt sich hier dein Hotspot-Profil."
                    : `No matches for ${[...activeTags].map((t) => `"${t}"`).join(" + ")}`}
                </div>
              </div>
            )}
            {/* Hover preview — floating thumbnail card anchored to the dot/
                arrow midpoint. Position is computed in viewBox space, then
                translated to % of the wrapper so the card tracks correctly
                on responsive resizes. Pointer-events disabled so the card
                never steals the click. */}
            {hover?.item && (
              <div
                data-testid="heatmap-hover-card"
                className="absolute z-10 pointer-events-none animate-in fade-in zoom-in-95 duration-150"
                style={{
                  left: `${(hover.cx / VB_W) * 100}%`,
                  top: `${(hover.cy / VB_H) * 100}%`,
                  // Center-bottom-anchor the card above the hovered glyph;
                  // clamp via translate so the card stays inside the canvas
                  // even when hovering near the right/bottom edges.
                  transform: `translate(${hover.cx > VB_W * 0.7 ? "-100%" : (hover.cx < VB_W * 0.3 ? "0%" : "-50%")}, ${hover.cy > VB_H * 0.6 ? "-100%" : "8px"})`,
                  maxWidth: 220,
                }}
              >
                <div className="bg-card/95 backdrop-blur border border-pink-400/60 rounded-sm overflow-hidden shadow-xl shadow-pink-400/10">
                  {hover.item.source === "clip" ? (
                    // Match-clip hover → looped annotated MP4 (lazy-rendered)
                    <HoverPreview
                      clipId={hover.item.id}
                      className="w-full aspect-video"
                      fallbackLabel="render queued"
                    />
                  ) : hover.item.youtube_id ? (
                    <img
                      src={`https://i.ytimg.com/vi/${hover.item.youtube_id}/mqdefault.jpg`}
                      alt=""
                      className="block w-full h-auto"
                      style={{ aspectRatio: "16/9", objectFit: "cover" }}
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : null}
                  <div className="px-2.5 py-1.5 space-y-0.5">
                    <div className="font-display font-bold text-xs text-foreground truncate">
                      {hover.item.title || hover.item.id}
                    </div>
                    <div className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                      {hover.kind === "arrow" && hover.item.length_m != null && (
                        <span className="text-pink-300">
                          {hover.item.length_m.toFixed(1)} m
                          {hover.item.angle_deg != null && ` · ${Math.round(hover.item.angle_deg)}°`}
                        </span>
                      )}
                      {hover.item.side && <span>{hover.item.side}</span>}
                    </div>
                    {hover.item.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-1">
                        {hover.item.tags.slice(0, 4).map((t) => (
                          <span key={t} className="px-1 py-0 bg-pink-400/10 border border-pink-400/30 rounded-sm font-mono text-[8px] uppercase tracking-widest text-pink-300">
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="pt-1 font-mono text-[8px] uppercase tracking-widest text-emerald-400">
                      {hover.item.source === "clip"
                        ? "→ Click to open in Sequence Lab"
                        : "→ Click to open in calibrator"}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="mt-3 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            <span data-testid="heatmap-field-footer">
              {filtered.length} dot{filtered.length === 1 ? "" : "s"}
              {filteredVectors.length > 0 && ` · ${filteredVectors.length} arrow${filteredVectors.length === 1 ? "" : "s"}`}
              {" · field "}{dims.length}{" × "}{dims.width}{" m"}
              {(data?.n_replay_excluded || 0) > 0 && (
                <>
                  {" · "}
                  <span
                    data-testid="heatmap-replay-excluded"
                    className="text-rose-300"
                    title="Pässe aus Replay-Shots werden automatisch aus Heatmap + Statistik ausgeblendet, damit nichts doppelt zählt (Shot-Klassen im Anker-Sammelmodus)."
                  >
                    {data.n_replay_excluded} replay ausgeblendet
                  </span>
                </>
              )}
              {profileMixed ? (
                <>
                  {" · "}
                  <span
                    data-testid="heatmap-field-profile-mixed"
                    className="text-amber-300"
                    title="Multiple field profiles in this view — the canvas reflects the WFDF Adult default to keep scaling consistent across mixed divisions."
                  >
                    mixed profiles
                  </span>
                </>
              ) : profileLabel ? (
                <>
                  {" · "}
                  <span
                    data-testid="heatmap-field-profile-label"
                    className="text-emerald-300"
                    title="Aggregated field profile — all contributing sequences share this division."
                  >
                    {profileLabel}
                  </span>
                </>
              ) : null}
            </span>
            <span className="flex items-center gap-2.5">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-pink-400" />
                brick play
              </span>
              {(data.vectors || []).length > 0 && (
                <span className="flex items-center gap-1.5">
                  <svg width="16" height="6" viewBox="0 0 16 6">
                    <line x1="0" y1="3" x2="13" y2="3" stroke="rgba(255,89,178,0.9)" strokeWidth="1.6" />
                    <path d="M 13 0 L 16 3 L 13 6 Z" fill="rgba(255,89,178,0.95)" />
                  </svg>
                  pass vector
                </span>
              )}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
